import React, { useState, useEffect } from 'react';
import { useApi } from '../../hooks/useApi';
import { useClub } from '../../hooks/useClub';
import { useAuth } from '../../hooks/useAuth';
import MemberTimeline from '../t2m/MemberTimeline';

// ─── Constants ────────────────────────────────────────────────────────────────

const TRIAL_ASK_RESULTS = [
  'Sold Trial',
  'Sold Membership',
  'Sold Punch Card',
  // Reordered 9/19/26 (BK) — sales-first, same waterfall priority as the
  // training script's "Why This Matters" section: close the trial first;
  // if that's a no today, the very next best outcome is getting them
  // booked into something else, not a generic follow-up flag. Reuses the
  // exact REENGAGE_RESULTS string/behavior — logPB101Outcome's isBooked
  // check already matches it regardless of which list it came from.
  'Booked — Coming Back',
  'Sales Follow-Up Needed',
  // Kanban Stage 4 (BK, PB101 pipeline redesign 9/17/26) — enjoyed PB101 but
  // isn't ready to commit. FD logs this to hand the record to Inside Sales'
  // nurture queue (see getPB101KanbanBoard in queries.js); distinct from
  // "Sales Follow-Up Needed", which is a hotter, near-term handoff.
  'Not Ready Yet',
  'Contacted — Waiting Response',
  'Declined — Price',
  'Declined — Timing',
  'Declined — Not Ready',
  'Declined — Prefers Drop In',
  'Declined — Already Plays Elsewhere',
  'Declined — Not Enough Time to Commit',
  'Failed to Ask',
  'No Show',
];

// Reach-out to already-registered PB101 people (Register + Open Play Invite,
// OP Invite Sent, Confirmed, Day Before) is a "did they want Open Play"
// conversation, not a trial ask — TRIAL_ASK_RESULTS was being reused here
// by mistake (BK, 9/17/26: logging "Declined — Price" or "Sold Trial"
// against someone who hasn't attended a session yet never made sense).
// Labels made explicit about what's actually booked/declined (BK, 9/18/26)
// — "Booked"/"Declined" alone read ambiguous next to a trial-ask outcome
// list. Renaming these means logPB101Outcome's isBooked/isDeclined checks
// (queries.js) must match these exact strings — kept in sync there.
const OPEN_PLAY_INVITE_RESULTS = [
  'Booked FREE Open Play',
  'Booked FREE Open Play — Different Beginner Session',
  'Declined FREE Open Play',
  'Contacted — Waiting Response',
];

// No-Show Re-Invite / Free Play Re-Engage (BK, 9/19/26) — TRIAL_ASK_RESULTS
// was being reused here too, same mistake class as OPEN_PLAY_INVITE_RESULTS
// above: this is a reschedule/re-invite conversation, not a live trial ask,
// so "Failed to Ask"/"No Show" don't apply (the card itself already IS a
// no-show) and the whole list read sale-first when it should read
// re-engage-first. A sale can still genuinely happen on this call — BK: "I
// guess any of those are possible" — so Sold/Declined stay available, just
// ordered after the outcomes that actually fit most of these calls. String
// values reuse existing ones where possible (Sold *, Contacted — Waiting
// Response, Declined — * prefix) so no backend classification changes are
// needed — logPB101Outcome's isDeclined/isTrialAskDeclined already match on
// prefix, and 'Booked — Coming Back' is intentionally NOT a special-cased
// outcome: once they actually re-register, the new registration is its own
// separate record (per the multi-visit model) and getPB101KanbanBoard's
// has_newer_record check retires this old card automatically — this option
// is just an honest log of what was said, not a state machine trigger.
const REENGAGE_RESULTS = [
  'Booked — Coming Back',
  'Declined — Not Interested',
  'Contacted — Waiting Response',
  'Sold Trial',
  'Sold Membership',
  'Sold Punch Card',
  'Declined — Price',
  'Declined — Timing',
  'Declined — Not Ready',
  'Declined — Prefers Drop In',
  'Declined — Already Plays Elsewhere',
  'Declined — Not Enough Time to Commit',
];

// Phases that are still working the pre-session Open Play invite, per
// getTaskPhase in queries.js — everything before the session itself happens.
const PRESESSION_INVITE_PHASES = ['presend', 'dayBefore', 'confirmed', 'opNudge', 'morningOf'];

const SALE_TYPES = [
  'Trial Sold', 'Membership Sold', 'Sold Punch Card', 'No',
];

const PLAN_TYPES_BASE = [
  'Unlimited Nationwide — Full Price',
  'Unlimited Nationwide — 10% Discount (Closer)',
  'Unlimited Club Only — 20% Discount (Closer)',
  'Social Membership',
  'Junior Membership',
  'Punch Card',
  'Trial Offer',
];

const PLAN_TYPES_DEN = [
  'Unlimited Nationwide — Full Price',
  'Unlimited Nationwide — 10% Discount (Closer)',
  'Unlimited Club Only — 20% Discount (Closer)',
  'Social Membership',
  'Junior Membership',
  'Punch Card',
  'Trial Offer',
  'Summer Slam — $49/mo Daytime Club',
  'Summer Slam — $65/mo Social',
  'Summer Slam — $79/mo Adult Unlimited',
];

// GAP-P8: timing guidance
const TIMING_GUIDANCE = {
  'Within 4 Hours': { text: "Failed to ask or no-show — reach out same day while it's fresh", note: '8am–8pm only', color: '#ef4444' },
  '24 Hours':       { text: 'Face-to-face decline — give space, plant a seed next day', color: '#f97316' },
  '48 Hours':       { text: 'Soft interest — give a day, re-engage before momentum fades', color: '#f97316' },
  '3 Days':         { text: 'Needs time to decide — check back mid-week', color: '#eab308' },
  '1 Week':         { text: "Timing objection — follow up when they said they'd be ready", color: '#6b7280' },
  'None':           { text: 'Lost, DNC, or already converted', color: '#6b7280' },
};

const CONTACT_METHOD_WHY = {
  text: "Low friction for someone who just attended — they're in a positive mindset. Keep it brief and action-oriented.",
  call: "More personal for re-offer situations where you need a real conversation about value.",
  email: "Lowest-engagement channel. Use only as a final follow-up after text and call.",
};

const SCRIPT_TIMING_WHY = {
  pb101_presession_5day:    "Sending before the session builds anticipation and increases attendance rate. One send is enough at 5+ days.",
  pb101_presession_1day:    "Day-before reminder significantly improves show rates. Highest-impact pre-session touch.",
  pb101_op_invite_registration: "Send immediately after registration — this is the Open Play invite. Strike while momentum is high.",
  pb101_op_followup_24hr:   "24-48hr follow-up if no response to the Open Play invite. Last chance before the event.",
  pb101_op_morning_of:      "Morning of the event — confirm they're coming. Stop all outreach 2 hours before event start.",
  pb101_postsession_ask:    "Best time to offer a trial is immediately after the session while the experience is fresh.",
  pb101_followup_1:         "First follow-up after a session where no trial was sold. Soft re-offer — don't be pushy.",
  pb101_followup_2:         "Final attempt. If no response after this, the record auto-closes.",
  pb101_trial_asked_stayed: "They stayed for Open Play — momentum is high. Strike while the energy is fresh.",
  pb101_trial_ask_notstayed:"They attended but didn't stay. Re-engage with a softer ask.",
  pb101_op_reengage:        "Past PB101 attendee — re-open the door without pressure. Reference the original session.",
};

// ─── Date helpers ─────────────────────────────────────────────────────────────

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}
function formatDateISO(date) { return date.toISOString().split('T')[0]; }
function formatDateDisplay(iso) {
  if (!iso) return '';
  return new Date(iso + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

const QUICK_PICKS = [
  { label: 'Today',     days: 0 },
  { label: 'Tomorrow',  days: 1 },
  { label: '+3 days',   days: 3 },
  { label: '+1 week',   days: 7 },
];

// ─── MiniCalendar ─────────────────────────────────────────────────────────────

function MiniCalendar({ selectedDate, minDate, onSelect }) {
  const today = new Date();
  const init  = selectedDate ? new Date(selectedDate + 'T12:00:00') : today;
  const [viewYear,  setViewYear]  = useState(init.getFullYear());
  const [viewMonth, setViewMonth] = useState(init.getMonth());

  const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const DAYS   = ['Su','Mo','Tu','We','Th','Fr','Sa'];

  function prevMonth() { if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y-1); } else setViewMonth(m => m-1); }
  function nextMonth() { if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y+1); } else setViewMonth(m => m+1); }

  const firstDay    = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth+1, 0).getDate();
  const minISO      = minDate || formatDateISO(today);
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div style={{ background: '#0d1117', border: '1px solid #333', borderRadius: 8, padding: 12, userSelect: 'none' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <button onClick={prevMonth} style={{ background: 'none', border: 'none', color: '#888', fontSize: 16, cursor: 'pointer', padding: '0 6px' }}>‹</button>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#e5e5e5' }}>{MONTHS[viewMonth]} {viewYear}</span>
        <button onClick={nextMonth} style={{ background: 'none', border: 'none', color: '#888', fontSize: 16, cursor: 'pointer', padding: '0 6px' }}>›</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 4 }}>
        {DAYS.map(d => <div key={d} style={{ textAlign: 'center', fontSize: 10, color: '#555', fontWeight: 700, padding: '2px 0' }}>{d}</div>)}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
        {cells.map((day, i) => {
          if (!day) return <div key={`e${i}`} />;
          const iso        = `${viewYear}-${String(viewMonth+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
          const isSelected = iso === selectedDate;
          const isToday    = iso === formatDateISO(today);
          const isPast     = iso < minISO;
          return (
            <button key={iso} disabled={isPast} onClick={() => !isPast && onSelect(iso)} style={{
              background: isSelected ? '#facc15' : isToday ? '#facc1520' : 'transparent',
              color: isSelected ? '#000' : isPast ? '#333' : isToday ? '#facc15' : '#ccc',
              border: isSelected ? 'none' : isToday ? '1px solid #facc1540' : '1px solid transparent',
              borderRadius: 5, padding: '5px 2px', fontSize: 12,
              cursor: isPast ? 'not-allowed' : 'pointer',
              fontWeight: isSelected ? 700 : 400, textAlign: 'center',
            }}>{day}</button>
          );
        })}
      </div>
    </div>
  );
}

// ─── FollowUpScheduler ────────────────────────────────────────────────────────

function FollowUpScheduler({ value, onChange, required }) {
  const [quickPick,    setQuickPick]    = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const today = new Date();

  function selectQuick(pick) {
    setQuickPick(pick.label);
    setShowCalendar(false);
    onChange({ ...value, date: formatDateISO(addDays(today, pick.days)) });
  }
  function handleCalendarSelect(iso) {
    setShowCalendar(false);
    setQuickPick('custom');
    onChange({ ...value, date: iso });
  }

  const borderColor = required && !value.date ? '#ef4444' : '#facc1530';

  return (
    <div style={{ background: '#0d1117', border: `1px solid ${borderColor}`, borderRadius: 8, padding: '12px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#facc15' }}>📅 Schedule Follow-Up</span>
        {required && <span style={{ fontSize: 10, color: '#ef4444', fontWeight: 700 }}>REQUIRED</span>}
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
        {QUICK_PICKS.map(pick => (
          <button key={pick.label} onClick={() => selectQuick(pick)} style={{
            fontSize: 11, padding: '4px 10px', borderRadius: 4, cursor: 'pointer',
            border: `1px solid ${quickPick === pick.label ? '#facc15' : '#333'}`,
            background: quickPick === pick.label ? '#facc1515' : '#111',
            color: quickPick === pick.label ? '#facc15' : '#888',
            fontWeight: quickPick === pick.label ? 700 : 400,
          }}>{pick.label}</button>
        ))}
        <button onClick={() => { setQuickPick('custom'); setShowCalendar(p => !p); }} style={{
          fontSize: 11, padding: '4px 10px', borderRadius: 4, cursor: 'pointer',
          border: `1px solid ${quickPick === 'custom' ? '#facc15' : '#333'}`,
          background: quickPick === 'custom' ? '#facc1515' : '#111',
          color: quickPick === 'custom' ? '#facc15' : '#888',
        }}>📅 Pick date</button>
      </div>
      {showCalendar && (
        <div style={{ marginBottom: 10 }}>
          <MiniCalendar selectedDate={value.date} minDate={formatDateISO(today)} onSelect={handleCalendarSelect} />
        </div>
      )}
      {value.date && (
        <div style={{ marginTop: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <span style={{ fontSize: 12, color: '#facc15', fontWeight: 600 }}>✓ {formatDateDisplay(value.date)}</span>
            <button onClick={() => { onChange({ ...value, date: null, time: null }); setQuickPick(null); }} style={{
              background: 'none', border: 'none', color: '#666', fontSize: 11, cursor: 'pointer',
            }}>✕ clear</button>
          </div>
          {/* Time of day selector — 30 min increments, matches T2M/Lead Engine */}
          <div style={{ marginTop: 4 }}>
            <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Time (optional):</div>
            <select
              value={value.time || ''}
              onChange={e => onChange({ ...value, time: e.target.value || null })}
              style={{
                background: '#111', border: '1px solid #333', color: value.time ? '#facc15' : '#666',
                borderRadius: 5, padding: '5px 10px', fontSize: 12, cursor: 'pointer', width: '100%',
              }}
            >
              <option value=''>Any time</option>
              {[
                '8:00 AM','8:30 AM','9:00 AM','9:30 AM','10:00 AM','10:30 AM',
                '11:00 AM','11:30 AM','12:00 PM','12:30 PM','1:00 PM','1:30 PM',
                '2:00 PM','2:30 PM','3:00 PM','3:30 PM','4:00 PM','4:30 PM',
                '5:00 PM','5:30 PM','6:00 PM','6:30 PM','7:00 PM','7:30 PM','8:00 PM',
              ].map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Script context key ───────────────────────────────────────────────────────

function scriptKeyForRecord(record) {
  if (!record.sessionDate && !record.sessionDateRaw) return 'pb101_postsession_ask';
  const today = new Date(); today.setHours(0,0,0,0);
  const session = new Date(record.sessionDateRaw || record.sessionDate); session.setHours(0,0,0,0);
  const diff = Math.round((session - today) / 86400000);

  // Pre-session
  if (diff > 1)  return 'pb101_op_invite_registration';
  if (diff === 1) return 'pb101_presession_1day';
  if (diff === 0 && !record.attendedLtp) return 'pb101_op_morning_of';

  // No-Show / Win-Back — well past the normal follow-up window (BK,
  // 9/18/26: caught a genuine no-show being served "I hope you had a great
  // time!" language, which makes no sense for someone who never showed up).
  // This function had never been updated with the No-Show/Win-Back day
  // thresholds getTaskPhase (queries.js) and scriptCueForSection
  // (PB101Tasks.jsx) already use — it's a separate, independent script-key
  // implementation that drifted out of sync when those stages were split.
  if (diff < 0 && -diff > 21) {
    const attended = record.attendedLtp === true || record.attendedLtp === 'true';
    if (!attended) {
      return (-diff) > 42 ? 'pb101_no_show_reinvite_stale' : 'pb101_no_show_reinvite_recent';
    }
    return 'pb101_op_reengage';
  }

  // Re-offer takes priority
  if (diff < 0 && record.trialOfferedAgain) return 'pb101_op_reengage';

  // Post-session — context-aware on attendance
  if (record.attendedLtp === false) return 'pb101_followup_1';
  if (record.attendedLtp && record.stayedForPlay) return 'pb101_trial_asked_stayed';
  if (record.attendedLtp && record.stayedForPlay === false) return 'pb101_trial_ask_notstayed';
  if (diff === 0) return 'pb101_postsession_ask';

  // Follow-up cadence
  const attempt = record.attempt || 0;
  return attempt <= 1 ? 'pb101_followup_1' : 'pb101_followup_2';
}

// ─── Script Panel ─────────────────────────────────────────────────────────────

function ScriptPanel({ record, clubId, role, userName, clubName }) {
  const { get } = useApi();
  const [script,     setScript]     = useState(null);
  const [allScripts, setAllScripts] = useState([]);
  const [collapsed,  setCollapsed]  = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [copied,     setCopied]     = useState(false);
  const [whyOpen,    setWhyOpen]    = useState({});

  const repName = role === 'fd' ? `The Picklr ${clubName || ''}` : (userName || '[Rep Name]');

  // [Day]/[Time] were never filled at all (BK, 9/18/26 — noticed the copied
  // script still said "on [Day] at [Time]!" verbatim). PB101 sessions are
  // fixed Wed 6PM / Sat 10AM (same convention as scriptCueForSection in
  // PB101Tasks.jsx) — real session date is on record.sessionDateRaw, so
  // fill it in directly rather than leaving a literal bracket placeholder.
  // Falls back to a clean phrase (not a dangling "at " with nothing after
  // it) when the date is missing or isn't a real session day.
  // isOpenPlayTime (BK, 9/18/26): Free Open Play runs AFTER the PB101 class,
  // not at the same time — only pb101_op_reengage's [Day]/[Time] actually
  // refers to Open Play itself; every other script's [Day]/[Time] (session
  // reminders, no-show re-invites to a new PB101 spot) means the class
  // itself, so those stay at the class start time.
  function dayTimeFromSession(sessionDateRaw, isOpenPlayTime) {
    if (!sessionDateRaw) return { day: null, time: null };
    const dow = new Date(sessionDateRaw + 'T12:00:00').getDay();
    if (dow === 3) return { day: 'Wednesday', time: isOpenPlayTime ? '7:00 PM' : '6:00 PM' };
    if (dow === 6) return { day: 'Saturday', time: isOpenPlayTime ? '11:00 AM' : '10:00 AM' };
    return { day: null, time: null };
  }

  function interpolate(body) {
    if (!body) return body;
    const { day, time } = dayTimeFromSession(record.sessionDateRaw, script?.script_key === 'pb101_op_reengage');
    let out = day
      ? body.replace(/\[Day\]/g, day).replace(/\[Time\]/g, time)
      : body.replace(/\[Day\] at \[Time\]/g, 'your upcoming session')
            .replace(/\[Day\]/g, 'the upcoming session')
            .replace(/\[Time\]/g, '');
    return out
      .replace(/\[Name\]/g, record.name?.split(' ')[0] || '[Name]')
      .replace(/\[Rep Name\]/g, repName)
      .replace(/\[Club Name\]/g, clubName || '[Club Name]');
  }

  useEffect(() => {
    // GAP: only fetch pb101 scripts
    get('/scripts?module=pb101')
      .then(data => {
        const scripts = data.scripts || [];
        setAllScripts(scripts);
        const key = scriptKeyForRecord(record);
        const clubMatch   = scripts.find(s => s.script_key === key && s.scope === 'club' && s.location_id === clubId);
        const globalMatch = scripts.find(s => s.script_key === key && s.scope !== 'club');
        setScript(clubMatch || globalMatch || null);
      })
      .catch(() => {});
  }, [record.id]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleCopy() {
    if (!script?.body) return;
    navigator.clipboard.writeText(interpolate(script.body)).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    });
  }

  if (!script) return null;

  const timingWhy = SCRIPT_TIMING_WHY[script.script_key] || script.why_context;
  const methods = Array.isArray(script.contact_method_order) && script.contact_method_order.length > 0
    ? script.contact_method_order : null;

  return (
    <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px', marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: collapsed ? 0 : 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>📋 {script.name}</span>
          {script.scope === 'club' && (
            <span style={{ background: 'rgba(234,179,8,0.2)', color: 'var(--yellow)', fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4 }}>CLUB OVERRIDE</span>
          )}
        </div>
        <button style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 12 }} onClick={() => setCollapsed(v => !v)}>
          {collapsed ? '▾ Show' : '▴ Hide'}
        </button>
      </div>

      {!collapsed && (
        <>
          {timingWhy && (
            <div style={{ background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 6, padding: '8px 12px', marginBottom: 10 }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: '#60a5fa', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>💡 WHY THIS SCRIPT</div>
              <div style={{ fontSize: 12, color: 'var(--text)', lineHeight: 1.5 }}>{timingWhy}</div>
            </div>
          )}

          {methods && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Contact Order</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                {methods.map((method, i) => {
                  const isOpen = whyOpen[method];
                  const icon  = method === 'text' ? '💬' : method === 'call' ? '📞' : '📧';
                  const color = method === 'call' ? '#f97316' : method === 'text' ? '#60a5fa' : 'var(--text-muted)';
                  return (
                    <div key={method} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        {i > 0 && <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>→</span>}
                        <span style={{ background: `${color}22`, color, border: `1px solid ${color}`, fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4 }}>
                          {icon} {method.toUpperCase()}
                        </span>
                        {CONTACT_METHOD_WHY[method] && (
                          <button style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--text-muted)', borderRadius: 4, padding: '1px 6px', fontSize: 9, cursor: 'pointer' }}
                            onClick={() => setWhyOpen(p => ({ ...p, [method]: !p[method] }))}>
                            {isOpen ? 'Less' : 'Why?'}
                          </button>
                        )}
                      </div>
                      {isOpen && (
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic', lineHeight: 1.5, maxWidth: 300, background: 'var(--surface-3)', padding: '6px 10px', borderRadius: 6 }}>
                          {CONTACT_METHOD_WHY[method]}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.6, padding: '8px 12px', background: 'var(--surface-3)', borderRadius: 6, marginBottom: 8, whiteSpace: 'pre-wrap' }}>
            {interpolate(script.body)}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <button style={{ background: 'var(--yellow)', color: '#000', border: 'none', borderRadius: 5, padding: '4px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }} onClick={handleCopy}>
              {copied ? '✓ Copied' : '📋 Copy Script'}
            </button>
            <button style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--text-muted)', borderRadius: 5, padding: '4px 12px', fontSize: 12, cursor: 'pointer' }}
              onClick={() => setPickerOpen(p => !p)}>
              ↕ Switch script
            </button>
          </div>

          {pickerOpen && (
            <div style={{ marginTop: 8, border: '1px solid #222', borderRadius: 6, overflow: 'hidden' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#666', textTransform: 'uppercase', letterSpacing: '0.06em', padding: '6px 10px', background: '#1a1a1a', borderBottom: '1px solid #222' }}>
                PB101 Scripts — tap to switch
              </div>
              {allScripts.map((s, i) => (
                <button key={s.script_id || i} onClick={() => { setScript(s); setPickerOpen(false); }} style={{
                  width: '100%', textAlign: 'left', padding: '7px 10px',
                  background: s.script_id === script.script_id ? 'rgba(234,179,8,0.1)' : '#111',
                  border: 'none', borderBottom: i < allScripts.length - 1 ? '1px solid #1a1a1a' : 'none',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#e5e5e5' }}>{s.name}</div>
                    {s.timing && <div style={{ fontSize: 10, color: '#666', marginTop: 1 }}>{s.timing}</div>}
                  </div>
                  {s.script_id === script.script_id && (
                    <span style={{ fontSize: 9, fontWeight: 700, color: '#facc15', background: 'rgba(234,179,8,0.15)', padding: '1px 5px', borderRadius: 3 }}>CURRENT</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Main Modal ───────────────────────────────────────────────────────────────

export default function PB101OutcomeModal({ record, onClose, onSaved }) {
  const { post } = useApi();
  const { currentClub } = useClub();
  const { role, user } = useAuth();
  const isDenton = currentClub?.location_id === 'DEN';
  const PLAN_TYPES = isDenton ? PLAN_TYPES_DEN : PLAN_TYPES_BASE;
  const outcomeOptions = PRESESSION_INVITE_PHASES.includes(record.taskPhase)
    ? OPEN_PLAY_INVITE_RESULTS
    : (record.taskPhase === 'noShow' || record.taskPhase === 'winBack')
      ? REENGAGE_RESULTS
      : TRIAL_ASK_RESULTS;

  // presetOutcome (PB101Kanban drag-and-drop, 9/18/26) — lets a drop onto an
  // unambiguous target column (e.g. Not Ready Yet) pre-select the outcome
  // without skipping the modal — contact method/notes/Save are still
  // required, nothing saves just from the drag itself.
  const [outcome,          setOutcome]          = useState(record.presetOutcome || '');
  const [contactMethods,   setContactMethods]    = useState([]);

  function toggleContactMethod(method) {
    setContactMethods(prev =>
      prev.includes(method) ? prev.filter(m => m !== method) : [...prev, method]
    );
  }
  const [saleType,         setSaleType]          = useState('');
  const [planType,         setPlanType]          = useState('');
  const [saveError,        setSaveError]          = useState('');
  const [trialOfferedAgain,setTrialOfferedAgain] = useState(record.trialOfferedAgain || false);
  const [notes,            setNotes]             = useState('');
  const [followUp,         setFollowUp]          = useState({ date: null });
  const [scheduleFollowUp, setScheduleFollowUp]  = useState(false);
  const [saving,           setSaving]            = useState(false);
  const [hotFlag,          setHotFlag]           = useState(false);
  const [hotNote,          setHotNote]           = useState('');

  // ─── Sprint 19.7 — Log Note mode ─────────────────────────────────────────
  // Lets a rep log a freeform contact note without picking an outcome or
  // touching any existing follow-up. Writes to outcome_log only — see
  // logContactNote in queries.js. Independent of the outcome flow below.
  const [noteMode,          setNoteMode]          = useState(false);
  const [noteText,          setNoteText]          = useState('');
  const [noteContactMethods, setNoteContactMethods] = useState([]);
  const [noteSaving,        setNoteSaving]        = useState(false);
  const [noteError,         setNoteError]         = useState('');

  function toggleNoteContactMethod(method) {
    setNoteContactMethods(prev =>
      prev.includes(method) ? prev.filter(m => m !== method) : [...prev, method]
    );
  }

  async function handleSaveNote() {
    setNoteError('');
    if (!noteText.trim()) { setNoteError('Please enter a note.'); return; }
    setNoteSaving(true);
    try {
      await post('/contact/note', {
        module: 'pb101',
        refId: record.id,
        notes: noteText,
        contactMethods: noteContactMethods,
      });
      onSaved(null);
    } catch (e) {
      console.error(e);
      setNoteError(e.message || 'Failed to save note.');
    } finally {
      setNoteSaving(false);
    }
  }

  // Derive flags from outcome
  const isSold         = ['Sold Trial','Sold Membership','Sold Punch Card'].includes(outcome);
  const isSalesHandoff = outcome === 'Sales Follow-Up Needed';
  const isWaitingResponse = outcome === 'Contacted — Waiting Response';
  const isNoShow       = outcome === 'No Show';
  const isDeclined     = outcome.startsWith('Declined') || outcome === 'Failed to Ask';
  const isLost         = outcome === 'Lost — No Response';
  const needsFollowUp  = isDeclined || isNoShow || outcome === 'Interested — Follow Up' || outcome === 'Attempted — No Answer';
  const needsPlan      = isSold;

  // Auto-set follow-up date for Sales Follow-Up Needed
  useEffect(() => {
    if (isSalesHandoff && !followUp.date) {
      setFollowUp({ date: formatDateISO(new Date()) });
    }
  }, [isSalesHandoff]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSave() {
    setSaveError('');
    if (!outcome) { setSaveError('Please select an outcome.'); return; }
    if (contactMethods.length === 0) { setSaveError('Please select at least one contact method.'); return; }
    if (needsPlan && !planType) { setSaveError('Please select a plan type.'); return; }
    if (isSalesHandoff && !followUp.date) { setSaveError('Please set a follow-up date for Sales Follow-Up.'); return; }
    setSaving(true);
    try {
      const result = await post('/pb101/outcome', {
        recordId:         record.id,
        outcome,
        saleType:         saleType || undefined,
        planType:         planType || undefined,
        trialOfferedAgain: trialOfferedAgain || undefined,
        followupDate:     followUp.date || undefined,
        followupTime:     followUp.time || undefined,
        notes,
        contactMethods,
        contactMethod:    contactMethods[0] || undefined,
        hotFlag:          isWaitingResponse ? hotFlag : undefined,
        hotNote:          isWaitingResponse && hotFlag ? (hotNote || undefined) : undefined,
      });
      // GAP-P9 — a heads-up only, the booking already went through either
      // way (BK: "overbooking can be a club decision"). No toast system in
      // this app yet, and this is rare enough not to warrant building one
      // just for this — a plain alert is enough to make sure it's actually
      // seen before the modal closes.
      if (result?.capacityNote) alert(result.capacityNote);
      onSaved(outcome);
    } catch (e) {
      console.error(e);
      // 9/23/26 — was silently swallowed; a rejected save (e.g. Open Play at
      // capacity) looked identical to a successful one from the FD's side,
      // no indication anything went wrong. useApi now surfaces the backend's
      // real message (see useApi.js), so show it here.
      setSaveError(e.message || 'Something went wrong saving this outcome.');
    } finally {
      setSaving(false);
    }
  }

  const saveLabel = saving ? 'Saving...'
    : isSalesHandoff ? '🔥 Flag for Sales'
    : isSold ? '✅ Log Conversion'
    : 'Save';

  const saveBtnStyle = isSalesHandoff
    ? { background: '#ef4444', borderColor: '#ef4444' }
    : isSold
    ? { background: '#22c55e', borderColor: '#22c55e', color: '#000' }
    : {};

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 860, width: '100%', maxHeight: '90vh', overflowY: 'auto', display: 'flex', flexDirection: 'row', gap: 20, alignItems: 'flex-start' }}>
      <div style={{ flex: 1, minWidth: 0 }}>

        {/* Header */}
        <div className="modal-title">Log Contact — {record.name}</div>
        <div className="card-meta" style={{ marginBottom: '1rem' }}>
          {record.sessionDate && <span>Session: {record.sessionDate}</span>}
          {record.phone       && <span>{record.phone}</span>}
          {record.attempt > 0 && <span>Attempt {record.attempt}/2</span>}
          {record.skillLevel  && <span>{record.skillLevel}</span>}
        </div>

        {/* Soft warning added 9/22/26 (McKinney pilot, BK) — a card gave no
            signal it had already been contacted, so more than one rep
            reached out to the same person the same day. Doesn't block
            saving, just makes the rep pause. */}
        {record.lastContactDate && (
          <div style={{
            background: 'rgba(167,139,250,0.1)', border: '1px solid rgba(167,139,250,0.3)',
            borderRadius: 6, padding: '8px 12px', marginBottom: 14, fontSize: 12, color: '#a78bfa',
          }}>
            ⚠️ Already contacted {new Date(record.lastContactDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} — check before reaching out again.
          </div>
        )}

        {/* Sprint 19.7 — Log Note toggle */}
        <div style={{ marginBottom: 14 }}>
          <button
            onClick={() => setNoteMode(m => !m)}
            style={{
              fontSize: 12, padding: '5px 12px', borderRadius: 5, cursor: 'pointer',
              border: `1px solid ${noteMode ? '#facc15' : 'var(--border)'}`,
              background: noteMode ? '#facc1515' : 'var(--surface-2)',
              color: noteMode ? '#facc15' : 'var(--text-muted)',
              fontWeight: noteMode ? 700 : 400,
            }}
          >
            📝 {noteMode ? 'Back to Log Outcome' : "Just log a note (keep existing follow-up)"}
          </button>
        </div>

        {noteMode ? (
          <div>
            <div className="field-group">
              <label className="field-label">Contact Method</label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {[
                  { value: 'text',      label: '💬 Text' },
                  { value: 'call',      label: '📞 Call' },
                  { value: 'email',     label: '📧 Email' },
                  { value: 'in-person', label: '🤝 In Person' },
                ].map(cm => {
                  const active = noteContactMethods.includes(cm.value);
                  return (
                    <button
                      key={cm.value}
                      onClick={() => toggleNoteContactMethod(cm.value)}
                      style={{
                        fontSize: 12, padding: '5px 12px', borderRadius: 5, cursor: 'pointer',
                        border: `1px solid ${active ? '#facc15' : 'var(--border)'}`,
                        background: active ? '#facc1515' : 'var(--surface-2)',
                        color: active ? '#facc15' : 'var(--text-muted)',
                        fontWeight: active ? 700 : 400,
                      }}
                    >
                      {cm.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="field-group">
              <label className="field-label">Note *</label>
              <textarea
                className="field-textarea"
                rows={4}
                placeholder="What happened, what did they say..."
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
              />
              <div style={{ fontSize: 10, color: '#555', marginTop: 3 }}>
                Saved to member contact history · does not change outcome or follow-up
              </div>
            </div>

            {noteError && (
              <div style={{ color: '#ef4444', fontSize: '0.85rem', margin: '0 0 0.5rem', padding: '0.5rem 0.75rem', background: 'rgba(239,68,68,0.1)', borderRadius: '6px' }}>
                {noteError}
              </div>
            )}
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSaveNote} disabled={noteSaving}>
                {noteSaving ? 'Saving...' : 'Save Note'}
              </button>
            </div>
          </div>
        ) : (
        <>
        {/* Context-aware script — PB101 scripts only */}
        <ScriptPanel record={record} clubId={record.clubId} role={role} userName={user?.name} clubName={currentClub?.name} />

        {/* HOT LEAD banner */}
        {isSalesHandoff && (
          <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.4)', borderRadius: 8, padding: '10px 14px', marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={{ background: '#ef4444', color: '#fff', fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4 }}>🔥 HOT LEAD</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#ef4444' }}>Sales Follow-Up Required</span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>
              This record will be flagged for Sales. Contact within <strong style={{ color: 'var(--text)' }}>4 hours</strong> — while interest is fresh.
            </div>
          </div>
        )}

        {/* Contact Method — multi-select */}
        <div className="field-group">
          <label className="field-label" style={{ color: '#ef4444' }}>Contact Method * <span style={{ fontSize: 10, fontWeight: 400, color: 'var(--text-muted)' }}>(select all that apply)</span></label>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {[
              { value: 'text',      label: '💬 Text' },
              { value: 'call',      label: '📞 Call' },
              { value: 'email',     label: '📧 Email' },
              { value: 'in-person', label: '🤝 In Person' },
            ].map(cm => {
              const active = contactMethods.includes(cm.value);
              return (
                <button
                  key={cm.value}
                  onClick={() => toggleContactMethod(cm.value)}
                  style={{
                    fontSize: 12, padding: '5px 12px', borderRadius: 5, cursor: 'pointer',
                    border: `1px solid ${active ? '#facc15' : 'var(--border)'}`,
                    background: active ? '#facc1515' : 'var(--surface-2)',
                    color: active ? '#facc15' : 'var(--text-muted)',
                    fontWeight: active ? 700 : 400,
                  }}
                >
                  {cm.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Outcome */}
        <div className="field-group">
          <label className="field-label">Outcome *</label>
          <select className="field-select" value={outcome}
            onChange={e => { setOutcome(e.target.value); setPlanType(''); setSaleType(''); }}>
            <option value="">Select outcome...</option>
            {outcomeOptions.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>

        {/* Plan type — only when sold */}
        {needsPlan && (
          <div className="field-group">
            <label className="field-label">Plan Type *</label>
            <select className="field-select" value={planType} onChange={e => setPlanType(e.target.value)}>
              <option value="">Select plan...</option>
              {PLAN_TYPES.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        )}

        {/* Sale type — only when sold */}
        {isSold && (
          <div className="field-group">
            <label className="field-label">Sale Type</label>
            <select className="field-select" value={saleType} onChange={e => setSaleType(e.target.value)}>
              <option value="">Select...</option>
              {SALE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        )}

        {/* Trial Offered Again checkbox */}
        {!isSold && outcome && (
          <div className="field-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={trialOfferedAgain} onChange={e => setTrialOfferedAgain(e.target.checked)} />
              <span className="field-label" style={{ margin: 0 }}>Trial Offered Again?</span>
            </label>
            {trialOfferedAgain && (
              <div style={{ marginTop: 6, fontSize: 11, color: '#f97316', fontStyle: 'italic' }}>
                ↗ Re-Offer script will surface next contact
              </div>
            )}
          </div>
        )}

        {/* GAP 1.10 — HOT flag + note, surfaces only for Contacted — Waiting Response */}
        {isWaitingResponse && (
          <div className="field-group" style={{ border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '10px 12px', background: 'rgba(239,68,68,0.05)' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={hotFlag} onChange={e => setHotFlag(e.target.checked)} />
              <span className="field-label" style={{ margin: 0, color: '#ef4444' }}>🔥 Flag HOT for Sales</span>
            </label>
            {hotFlag && (
              <textarea
                className="field-textarea"
                rows={2}
                placeholder='e.g. "husband and wife, both play regularly"'
                value={hotNote}
                onChange={e => setHotNote(e.target.value)}
                style={{ marginTop: 8 }}
              />
            )}
            <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>
              Moves to Lead Engine, sourced from PB101. HOT flag surfaces this to Josh with priority.
            </div>
          </div>
        )}

        {/* Follow-up scheduler — MiniCalendar (point 6). Auto-shown (and
            required for Sales Handoff) for the outcomes that already warrant
            it. For everything else — Booked, Sold Trial, any outcome that
            doesn't automatically need one — a general toggle lets a rep
            opt in anyway (BK, 9/17/26: "we need this across ALL modules,"
            matching the always-available toggle already built in Lead
            Engine's outcome modal, not gated to specific outcome values). */}
        {(needsFollowUp || isSalesHandoff || isWaitingResponse) ? (
          <div className="field-group">
            <FollowUpScheduler
              value={followUp}
              onChange={setFollowUp}
              required={isSalesHandoff}
            />
            {/* GAP-P8: timing guidance based on outcome */}
            {outcome && (() => {
              const timingKey = isSalesHandoff || isNoShow ? 'Within 4 Hours'
                : isDeclined && outcome.includes('Timing') ? '1 Week'
                : isDeclined ? '24 Hours' : null;
              const guidance = timingKey ? TIMING_GUIDANCE[timingKey] : null;
              return guidance ? (
                <div style={{ marginTop: 6, padding: '6px 10px', background: guidance.color + '11', border: `1px solid ${guidance.color}33`, borderRadius: 5, fontSize: 11, color: guidance.color, lineHeight: 1.4 }}>
                  💡 {guidance.text}{guidance.note ? ` · ${guidance.note}` : ''}
                </div>
              ) : null;
            })()}
          </div>
        ) : outcome && (
          <div className="field-group">
            <div
              onClick={() => setScheduleFollowUp(p => !p)}
              style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: scheduleFollowUp ? 10 : 0, cursor: 'pointer' }}
            >
              <div style={{ width: 36, height: 20, borderRadius: 10, background: scheduleFollowUp ? '#facc15' : '#333', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
                <div style={{ width: 16, height: 16, borderRadius: '50%', background: scheduleFollowUp ? '#000' : '#666', position: 'absolute', top: 2, left: scheduleFollowUp ? 18 : 2, transition: 'left 0.2s' }} />
              </div>
              <span style={{ fontSize: 13, color: scheduleFollowUp ? '#facc15' : '#888' }}>Schedule a follow-up</span>
            </div>
            {scheduleFollowUp && (
              <FollowUpScheduler value={followUp} onChange={setFollowUp} required={false} />
            )}
          </div>
        )}

        {/* Notes — always present, go to outcome_log → member timeline (point 7) */}
        <div className="field-group">
          <label className="field-label">Notes</label>
          <textarea
            className="field-textarea"
            rows={3}
            placeholder={isSalesHandoff
              ? "Add context for Sales — what did they say, what's their hesitation?"
              : "Notes saved to member history across all modules..."}
            value={notes}
            onChange={e => setNotes(e.target.value)}
          />
          <div style={{ fontSize: 10, color: '#555', marginTop: 3 }}>
            Saved to member contact history · visible across T2M, Lead Engine, PB101
          </div>
        </div>

        {saveError && (
          <div style={{ color: '#ef4444', fontSize: '0.85rem', margin: '0 0 0.5rem', padding: '0.5rem 0.75rem', background: 'rgba(239,68,68,0.1)', borderRadius: '6px' }}>
            {saveError}
          </div>
        )}
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saving}
            style={saveBtnStyle}
          >
            {saveLabel}
          </button>
        </div>
        </>
        )}
      </div>

      {/* Note history, shown to the side once the contact card is opened
          (BK, 9/22/26) — reuses the same shared MemberTimeline component
          T2M's record detail already uses, so a rep sees prior contacts
          (across PB101/T2M/Lead Engine, not just this module) right where
          they're about to log a new one, no navigating away needed. */}
      <div style={{
        width: 260, flexShrink: 0, borderLeft: '1px solid var(--border)',
        paddingLeft: 16, maxHeight: 'calc(90vh - 32px)', overflowY: 'auto',
      }}>
        <MemberTimeline memberId={record.memberId} memberName={record.name} compact maxHeight="calc(90vh - 80px)" />
      </div>
    </div>
    </div>
  );
}
